import deepEqual from 'deep-equal';
import React, { useState } from 'react';
import {
  AxisDomain,
  CartesianGrid,
  CartesianGridProps,
  ContentRenderer,
  LabelFormatter,
  Legend,
  LegendProps,
  Line,
  LineChart as ReLineChart,
  ResponsiveContainer,
  TickFormatterFunction,
  Tooltip,
  TooltipFormatter,
  TooltipProps,
  XAxis,
  XPadding,
  YAxis,
  YPadding,
} from 'recharts';
import getChartColor from '../chart/getChartColor';
import LineChartDot from './LineChartDot';
import { RechartsDotProps, RechartsOnClick } from './types';
import { getRechartsAxisDomains } from '../chart';

type YKey<T> = {
  key: keyof T;
  color?: string;
  name?: string;
};

interface Props<T> {
  height?: number | string;
  width?: number | string;
  onSelectDataPoint?: (point: T) => void;
  data?: T[];
  grid?: CartesianGridProps;
  x?: {
    key?: keyof T;
    formatter?: TickFormatterFunction;
    label?: string | number;
    name?: string | number;
    stroke?: string;
    padding?: XPadding;
    domain?: readonly [AxisDomain, AxisDomain];
  };
  y?: {
    keys: YKey<T>[];
    formatter?: TickFormatterFunction;
    label?: string | number;
    name?: string | number;
    stroke?: string;
    padding?: YPadding;
    domain?: readonly [AxisDomain, AxisDomain];
  };
  tooltip?: {
    content?: ContentRenderer<TooltipProps>;
    valueFormatter?: TooltipFormatter;
    labelFormatter?: LabelFormatter;
  };
  animate?: boolean;
  legend?: LegendProps;
}

export default function LineChart<DataInstance>({
  onSelectDataPoint,
  data = [],
  width = '100%',
  height = '100%',
  x,
  y,
  tooltip,
  animate = false,
  legend,
  grid,
}: Props<DataInstance>) {
  const [selectedPoint, setSeletedPoint] = useState<DataInstance | null>(null);

  const handleClick: RechartsOnClick<DataInstance> = (data: any) => {
    const point = data?.activePayload?.[0]?.payload;
    if (point && onSelectDataPoint) {
      setSeletedPoint(point);
      onSelectDataPoint(point);
    }
  };

  return (
    <ResponsiveContainer width={width} height={height}>
      <ReLineChart
        data={(data as unknown) as readonly object[]}
        onClick={handleClick}
      >
        {grid && <CartesianGrid {...grid} />}
        <XAxis
          dataKey={x?.key as string | undefined}
          domain={x?.domain || ['auto', 'auto']}
          tickFormatter={x?.formatter}
          type="number"
          label={x?.label}
          name={x?.name}
          stroke={x?.stroke}
          padding={x?.padding}
        />
        <YAxis
          domain={
            y?.domain || getRechartsAxisDomains<DataInstance>(data, y?.keys)
          }
          label={y?.label}
          tickFormatter={y?.formatter}
          name={y?.name}
          stroke={y?.stroke}
          padding={y?.padding}
        />
        {(y?.keys || []).map((yKey, i) => {
          const { key, color = getChartColor(i), name } = yKey;
          return (
            <Line
              type="monotone"
              name={name}
              key={key as string}
              dataKey={key as string}
              stroke={color}
              activeDot={{ r: 5 }}
              fill={color}
              connectNulls
              // for multiple lines, customer rendering of LineChartDot is broken
              // it shows dots at the top of the chart
              dot={
                onSelectDataPoint
                  ? (dotProps: RechartsDotProps<DataInstance>) => (
                      <LineChartDot
                        {...dotProps}
                        stroke={color}
                        // if data points non selectable, make all dots active
                        active={deepEqual(selectedPoint, dotProps.payload)}
                      />
                    )
                  : undefined
              }
              isAnimationActive={animate}
            />
          );
        })}
        <Tooltip
          labelFormatter={tooltip?.labelFormatter}
          formatter={tooltip?.valueFormatter}
          content={tooltip?.content}
          animationDuration={0}
        />
        {legend && <Legend {...legend} />}
      </ReLineChart>
    </ResponsiveContainer>
  );
}
